// src/pages/RealisationsPage.js
import React, { useState, useEffect } from 'react'; // <<< AJOUT DE useState, useEffect
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import './RealisationsPage.css';
import ProjectProcessSection from '../components/realisations/ProjectProcessSection';
import PortfolioCTA from '../components/realisations/PortfolioCTA';
import pb from '../lib/pocketbase';

function pbFileUrl(record, fileField, thumb) {
  try {
    if (!record || !record[fileField]) return '';
    return pb.files.getUrl(record, record[fileField], thumb ? { thumb } : undefined);
  } catch {
    return '';
  }
}

function RealisationsPage() {
  const [selectedCategoryId, setSelectedCategoryId] = useState('all');
  const [categories, setCategories] = useState([]);
  const [projects, setProjects] = useState([]);
  const [filteredProjects, setFilteredProjects] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchAll = async () => {
      setLoading(true);
      try {
        const [catsRes, projectsRes] = await Promise.all([
          pb.collection('site_realisations_categories').getList(1, 200, { sort: 'sort_order, name' }),
          pb.collection('site_realisations').getList(1, 200, { sort: 'sort_order, -created', filter: 'is_published = true', expand: 'category_id' }),
        ]);
        setCategories(catsRes?.items ?? []);
        setProjects(projectsRes?.items ?? []);
      } catch {
        setCategories([]);
        setProjects([]);
      } finally {
        setLoading(false);
      }
    };
    fetchAll();
  }, []);

  useEffect(() => {
    if (selectedCategoryId === 'all') {
      setFilteredProjects(projects);
    } else {
      setFilteredProjects(projects.filter(p => p.category_id === selectedCategoryId));
    }
    // Optionnel: scroll vers le haut de la grille de projets lors du filtrage
    // const projectGrid = document.querySelector('.projects-grid');
    // if (projectGrid) {
    //   window.scrollTo({ top: projectGrid.offsetTop - 80, behavior: 'smooth' }); // 80 est un offset pour la navbar
    // }
  }, [selectedCategoryId, projects]);

  // Helmet dynamic content
  const siteUrl = "https://infocolab.com";
  let pageTitle = "Nos Réalisations - Portfolio de Projets Web | INFO COLAB";
  let pageDescription = "Explorez le portfolio de INFO COLAB et découvrez nos réalisations en création de sites web, applications et design pour des clients variés.";
  let canonicalUrl = `${siteUrl}/realisations`;
  const ogImageUrl = `${siteUrl}/images/og-realisations.jpg`; // Assure-toi que cette image existe

  const selectedCategoryObj = selectedCategoryId === 'all'
    ? null
    : (categories.find(c => c.id === selectedCategoryId) || null);

  if (selectedCategoryObj?.name) {
    pageTitle = `Réalisations en ${selectedCategoryObj.name} | INFO COLAB`;
    pageDescription = `Découvrez nos projets dans la catégorie "${selectedCategoryObj.name}" réalisés par INFO COLAB.`;
    canonicalUrl = `${siteUrl}/realisations/categorie/${(selectedCategoryObj.slug || selectedCategoryObj.name).toLowerCase().replace(/\s+/g, '-')}`;
  }

  return (
    <>
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDescription} />
        <link rel="canonical" href={canonicalUrl} />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDescription} />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:image" content={ogImageUrl} />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="INFO COLAB" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={pageTitle} />
        <meta name="twitter:description" content={pageDescription} />
        <meta name="twitter:image" content={ogImageUrl} />
      </Helmet>

      <div className="container">
        <section className="realisations-page-section">
          <h1>{selectedCategoryObj?.name ? `Réalisations : ${selectedCategoryObj.name}` : 'Nos Réalisations'}</h1>
          <p className="intro">
            Explorez une sélection de projets que nous avons eu le plaisir de réaliser pour nos clients,
            démontrant notre expertise et notre engagement envers la qualité.
          </p>

          {/* BOUTONS DE FILTRE PAR CATÉGORIE */}
          {categories.length > 0 && ( // Affiche les filtres seulement si au moins une catégorie existe
            <div className="filter-controls">
              <button
                key="all"
                onClick={() => setSelectedCategoryId('all')}
                className={`btn filter-btn ${selectedCategoryId === 'all' ? 'active btn-primary' : 'btn-outline-secondary'}`}
              >
                Tous
              </button>
              {categories.map(category => (
                <button
                  key={category.id}
                  onClick={() => setSelectedCategoryId(category.id)}
                  className={`btn filter-btn ${selectedCategoryId === category.id ? 'active btn-primary' : 'btn-outline-secondary'}`}
                >
                  {category.name}
                </button>
              ))}
            </div>
          )}

          {loading ? (
            <p style={{ opacity: 0.8 }}>Chargement…</p>
          ) : filteredProjects.length > 0 ? (
            <div className="projects-grid">
              {filteredProjects.map(project => (
                <div key={project.slug || project.id} className="project-card">
                  <Link to={`/realisations/${project.slug}`} className="project-card-link-wrapper">
                    <div className="project-card-image">
                      <img
                        src={pbFileUrl(project, 'cover_image', '1200x0') || '/images/placeholder-default.jpg'}
                        alt={project.title}
                      />
                    </div>
                    <div className="project-card-content">
                      <h2>{project.title}</h2>
                      <p>{project.excerpt || "Cliquez pour en savoir plus..."}</p>
                      <span className="btn-details-look">Voir les détails &rarr;</span>
                    </div>
                  </Link>
                </div>
              ))}
            </div>
          ) : (
            <p className="no-results-message">
              Aucun projet trouvé.
            </p>
          )}

          <PortfolioCTA /> 
          
          <ProjectProcessSection />
        </section>
      </div>
    </>
  );
}

export default RealisationsPage;